import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { getTask, updateTaskStatus } from '../service/weexpertService';
import { useTranslation } from "react-i18next";
import { Button } from '@/components/ui/button';
import { InputWithAlert } from '@/components/ui/input-with-alert';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { formatInWarsaw, isExpiredInWarsaw } from '../utils/timezone';
import { toast } from '@/components/ui/use-toast';
import { getCurrentUser } from '../service/weexpertService';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { ROLES } from '@/constants';
import webSocketService from '../services/websocket';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, CheckCircle2, Clock, PlayCircle, AlertTriangle, ExternalLink, User, FileText } from 'lucide-react';

const getStatusDisplay = (status, t) => {
  const statusMap = {
    'PENDING': t('task_status_pending') || 'Pending',
    'IN_PROGRESS': t('task_status_in_progress') || 'In Progress',
    'COMPLETED': t('task_status_completed') || 'Completed',
    'OVERDUE': t('task_status_overdue') || 'Overdue'
  };
  return statusMap[status] || status;
};

const getTaskTypeDisplay = (type, t) => {
  const typeMap = {
    'TEXT': t('task_type_text') || 'Text',
    'FORM': t('task_type_form') || 'Form'
  };
  return typeMap[type] || type;
};

const TaskViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('tasks');
  const queryClient = useQueryClient();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const { data: user, isLoading: isUserLoading } = useQuery('currentUser', getCurrentUser);
  const { data: taskData, isLoading } = useQuery(['task', id], () => getTask(id));

  const task = taskData?.data;
  const userRole = user?.data?.roles;
  const isOwner = user?.data?.id === task?.assignedTo?.id;
  const isAdmin = userRole === ROLES.ADMIN || userRole === ROLES.SUPER_ADMIN;
  const isExpired = task && isExpiredInWarsaw(task.deadline) && task.status !== 'COMPLETED';
  const canUpdateStatus = (isOwner || isAdmin) && (!isExpired || isAdmin);
  const canEdit = (isOwner || isAdmin) && (!isExpired || isAdmin);

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    if (!id) return;

    // Connect to WebSocket service
    webSocketService.connect();

    // Handle task updates
    const handleTaskUpdate = (event) => {
      const updatedTask = event.detail;

      // Only invalidate if this is the current task
      if (updatedTask.id === parseInt(id)) {
        queryClient.invalidateQueries(['task', id]);
      }
    };

    // Handle task status updates
    const handleTaskStatusUpdate = (event) => {
      const updatedTask = event.detail;

      // Only invalidate if this is the current task
      if (updatedTask.id === parseInt(id)) {
        queryClient.invalidateQueries(['task', id]);
      }
    };

    // Add event listeners
    window.addEventListener('task:updated', handleTaskUpdate);
    window.addEventListener('task:status_updated', handleTaskStatusUpdate);

    // Cleanup function
    return () => {
      window.removeEventListener('task:updated', handleTaskUpdate);
      window.removeEventListener('task:status_updated', handleTaskStatusUpdate);
    };
  }, [id, queryClient]);

  const handleStatusUpdate = async (newStatus) => {
    if (!canUpdateStatus || newStatus === task.status) return;

    // Show warning for expired tasks
    if (isExpired && !isAdmin) {
      toast({
        title: t('task_expired') || "Task Expired",
        description: t('task_expired_cannot_update') || "This task has expired and can only be updated by administrators",
        variant: "destructive"
      });
      return;
    }

    setIsUpdatingStatus(true);
    try {
      await updateTaskStatus(id, newStatus);
      queryClient.invalidateQueries(['task', id]);
      queryClient.invalidateQueries('tasks');
      queryClient.invalidateQueries('taskNotifications');

      toast({
        title: t('success') || "Success",
        description: t('task_status_updated') || "Task status updated successfully",
        variant: "default"
      });
    } catch (error) {
      let errorMessage = t('task_status_update_failed') || "Failed to update task status";

      // Check if it's an expired task error
      if (error.response?.data?.message?.includes('expired')) {
        errorMessage = t('task_expired_cannot_update') || "This task has expired and can only be updated by administrators";
      }

      toast({
        title: t('error') || "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="w-4 h-4" />;
      case 'IN_PROGRESS':
        return <PlayCircle className="w-4 h-4" />;
      case 'COMPLETED':
        return <CheckCircle2 className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getLinkUrl = (link) => {
    switch (link.link_type) {
      case 'CLIENT':
        return `/admin/client/details/${link.id}`;
      case 'ONE_TIME_CONTRACT':
        return `/contracts/one-time/${link.id}`;
      default:
        return null;
    }
  };

  const getLinkLabel = (link) => {
    switch (link.link_type) {
      case 'CLIENT':
        return t('view_client') || 'View Client';
      case 'ONE_TIME_CONTRACT':
        return t('view_contract') || 'View Contract';
      default:
        return t('view_details') || 'View Details';
    }
  };

  const getLinkIcon = (link) => {
    switch (link.link_type) {
      case 'CLIENT':
        return <User className="w-4 h-4" />;
      case 'ONE_TIME_CONTRACT':
        return <FileText className="w-4 h-4" />;
      default:
        return <ExternalLink className="w-4 h-4" />;
    }
  };

  const renderTaskContent = () => {
    if (!task?.content) return 'No content available';

    if (task.taskType === 'TEXT') {
      // For text tasks, check if content is object with description
      if (typeof task.content === 'object' && task.content.description) {
        return task.content.description;
      }
      // Otherwise return as string
      return typeof task.content === 'string' ? task.content : JSON.stringify(task.content);
    } else if (task.taskType === 'FORM') {
      // For form tasks, content is JSON - we'll display it formatted
      if (typeof task.content === 'object') {
        // If it has links, exclude them from display
        const { links, ...contentWithoutLinks } = task.content;
        return JSON.stringify(contentWithoutLinks, null, 2);
      }
      return task.content;
    }

    return 'No content available';
  };

  if (isLoading || isUserLoading) {
    return (
      <Box sx={{ width: "100%", height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Stack sx={{ color: 'grey.500' }} spacing={2} direction="row">
          <CircularProgress color="grey" />
        </Stack>
      </Box>
    );
  }

  if (user && user.data && user.data.roles === 'CLIENT') {
    navigate('/404')
  }

  if (!task) {
    return (
      <Box sx={{ width: "100%", height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Task Not Found</h2>
          <p className="text-gray-600 mb-6">The task you're looking for doesn't exist or you don't have permission to view it.</p>
          <Button
            className="text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none"
            onClick={() => navigate('/admin/tasks')}
          >
            Go to Tasks
          </Button>
        </div>
      </Box>
    );
  }

  const isOverdue = new Date(task.deadline) < new Date() && task.status !== 'COMPLETED';
  const currentStatus = isOverdue && task.status !== 'COMPLETED' ? 'OVERDUE' : task.status;

  return (
    <div className="relative bg-grayLightMainBg px-8">
      {/* Sticky Header */}
      <div className="sticky left-0 top-16 z-10 w-full bg-grayLightMainBg">
        <div className="flex justify-between py-8 items-center">
          <div className="w-5/12 text-4xl">
            {t('task_details') || 'Task Details'}
          </div>
          <div className="w-6/12 flex justify-end">
            {canUpdateStatus && task.status !== 'COMPLETED' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="mr-2 text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none"
                    disabled={isUpdatingStatus}
                    title={isExpired && !isAdmin ? "Task has expired and can only be updated by administrators" : ""}
                  >
                    {getStatusIcon(task.status)}
                    <span className="ml-2">{getStatusDisplay(task.status, t)}</span>
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>{t('change_status') || 'Change Status'}</DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  {task.status !== 'PENDING' && (
                    <DropdownMenuItem
                      onClick={() => handleStatusUpdate('PENDING')}
                      className="cursor-pointer"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      {getStatusDisplay('PENDING', t)}
                    </DropdownMenuItem>
                  )}

                  {task.status !== 'IN_PROGRESS' && (
                    <DropdownMenuItem
                      onClick={() => handleStatusUpdate('IN_PROGRESS')}
                      className="cursor-pointer"
                    >
                      <PlayCircle className="w-4 h-4 mr-2" />
                      {getStatusDisplay('IN_PROGRESS', t)}
                    </DropdownMenuItem>
                  )}

                  {task.status !== 'COMPLETED' && (
                    <DropdownMenuItem
                      onClick={() => handleStatusUpdate('COMPLETED')}
                      className="cursor-pointer"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                      <span className="text-green-600">{getStatusDisplay('COMPLETED', t)}</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {canEdit && (
              <Button
                className="text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none"
                onClick={() => navigate(`/admin/task/edit/${task.id}`)}
                title={isExpired && !isAdmin ? "Task has expired and can only be edited by administrators" : ""}
              >
                {t('button_change') || 'Edit'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full py-8">
        {/* Task Title */}
        <div className="text-2xl pb-6">
          {task.title}
        </div>

        {/* Expired Task Warning */}
        {isExpired && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
              <div>
                <p className="text-red-800 font-medium">
                  {t('task_expired_warning') || 'This task has expired'}
                </p>
                <p className="text-red-600 text-sm mt-1">
                  {isAdmin
                    ? (t('task_expired_admin_message') || 'As an administrator, you can still edit this task.')
                    : (t('task_expired_user_message') || 'This task can only be edited by administrators.')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Information Section */}
        <div className="text-xl font-medium pb-6">
          {t('main_information_title') || 'Main Information'}
        </div>

        <div className="w-9/12">
          <div className="w-full flex justify-between">
            <div className="w-[49%]">
              <InputWithAlert
                label={t('task_title') || 'Title'}
                value={task.title}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />

              <InputWithAlert
                label={t('task_type') || 'Type'}
                value={getTaskTypeDisplay(task.taskType, t)}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />

              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  {t('status') || 'Status'}
                </label>
                <div className="h-12 bg-white border rounded-md px-3 flex items-center">
                  <div className={`flex items-center ${
                    currentStatus === 'COMPLETED' ? 'text-green-600' :
                    currentStatus === 'IN_PROGRESS' ? 'text-blue-600' :
                    currentStatus === 'OVERDUE' ? 'text-red-600' :
                    'text-yellow-600'
                  }`}>
                    {getStatusIcon(currentStatus)}
                    <span className="ml-2 font-medium">{getStatusDisplay(currentStatus, t)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-[49%]">
              <InputWithAlert
                label={t('assigned_to') || 'Assigned To'}
                value={`${task.assignedTo?.firstName || ''} ${task.assignedTo?.lastName || ''}`}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />

              <InputWithAlert
                label={t('assigned_by') || 'Assigned By'}
                value={`${task.assignedBy?.firstName || ''} ${task.assignedBy?.lastName || ''}`}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />

              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  {t('deadline') || 'Deadline'}
                </label>
                <div className={`h-12 bg-white border rounded-md px-3 flex items-center ${
                  isExpired ? 'border-red-300 bg-red-50' : ''
                }`}>
                  <div className={`flex items-center ${isExpired ? 'text-red-600' : ''}`}>
                    {isExpired && <AlertTriangle className="w-4 h-4 mr-2" />}
                    <span className={isExpired ? 'font-medium' : ''}>
                      {formatInWarsaw(task.deadline, 'dd.MM.yyyy HH:mm')} (Warsaw)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Task Content Section */}
          <div className="text-xl font-medium pt-14 pb-6">
            {t('task_content') || 'Task Content'}
          </div>

          <div className="w-full">
            <Textarea
              readOnly={true}
              disabled
              placeholder={t('task_content_placeholder') || 'Task content'}
              value={renderTaskContent()}
              className="min-h-32 bg-white font-mono text-sm"
            />
          </div>

          {/* Related Links Section */}
          {task.content?.links && task.content.links.length > 0 && (
            <>
              <div className="text-xl font-medium pt-14 pb-6">
                {t('related_links') || 'Related Links'}
              </div>
              <div className="w-full space-y-3">
                {task.content.links.map((link, index) => {
                  const url = getLinkUrl(link);
                  if (!url) return null;
                  
                  return (
                    <Link
                      key={index}
                      to={url}
                      className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <div className="text-blueText">
                        {getLinkIcon(link)}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {getLinkLabel(link)}
                        </p>
                        <p className="text-xs text-gray-500">
                          ID: {link.id}
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </Link>
                  );
                })}
              </div>
            </>
          )}

          {/* Timeline Section */}
          <div className="text-xl font-medium pt-14 pb-6">
            {t('timeline') || 'Timeline'}
          </div>

          <div className="w-full flex justify-between">
            <div className="w-[49%]">
              <InputWithAlert
                label={t('created_date') || 'Created'}
                value={formatInWarsaw(task.createdAt, 'dd.MM.yyyy HH:mm')}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />

              {task.updatedAt && (
                <InputWithAlert
                  label={t('updated_date') || 'Last Updated'}
                  value={formatInWarsaw(task.updatedAt, 'dd.MM.yyyy HH:mm')}
                  readOnly={true}
                  disabled
                  inputClassName={'h-12 mt-2 mb-4 bg-white'}
                />
              )}
            </div>

            <div className="w-[49%]">
              {task.completedAt && (
                <InputWithAlert
                  label={t('completed_date') || 'Completed'}
                  value={formatInWarsaw(task.completedAt, 'dd.MM.yyyy HH:mm')}
                  readOnly={true}
                  disabled
                  inputClassName={'h-12 mt-2 mb-4 bg-white'}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskViewPage;
